import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, LogOut, User } from 'lucide-react';
import CartIcon from './Cart/CartIcon';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import toast from 'react-hot-toast';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const menuItems = [
    { path: '/', label: 'Accueil' },
    { path: '/cours-particuliers', label: 'Cours Particuliers' },
    { path: '/flashcards', label: 'Flashcards SES' },
    { path: '/sujets-bac', label: 'Sujets Bac' },
    { path: '/ressources', label: 'Ressources Gratuites' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileMenuOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current && 
        profileButtonRef.current && 
        !profileMenuRef.current.contains(event.target as Node) &&
        !profileButtonRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fonction pour défiler vers le haut de la page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Composant Link personnalisé qui défile vers le haut lors du clic
  const ScrollToTopLink = ({ to, className, children }) => {
    return (
      <Link 
        to={to} 
        className={className}
        onClick={scrollToTop}
      >
        {children}
      </Link>
    );
  };

  return (
    <nav className="bg-white shadow-lg fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <ScrollToTopLink to="/" className="flex items-center space-x-3">
              <img
                src="/Logo SES.png"
                alt="Comprendre les SES"
                className="h-10 w-auto"
              />
              <span className="hidden md:block font-semibold text-[#323c5f] text-lg">
                Comprendre les SES
              </span>
            </ScrollToTopLink>
          </div>

          {/* Menu de navigation - Desktop */}
          <div className="hidden md:flex items-center">
            <div className="flex space-x-1">
              {menuItems.map((item) => (
                <ScrollToTopLink 
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? 'text-white bg-[#9a6ebd]'
                      : 'text-gray-600 hover:text-[#9a6ebd] hover:bg-[#9a6ebd]/10'
                  }`}
                >
                  {item.label}
                </ScrollToTopLink>
              ))}
            </div>

            {/* Actions - Desktop */}
            <div className="flex items-center space-x-6 ml-6 pl-6 border-l border-gray-200">
              <CartIcon />
              {currentUser ? (
                <div className="relative">
                  <button
                    ref={profileButtonRef}
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {currentUser.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt={currentUser.displayName || ''}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-[#9a6ebd] rounded-full flex items-center justify-center text-white">
                        {currentUser.displayName?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      {currentUser.displayName}
                    </span>
                  </button>

                  {isProfileMenuOpen && (
                    <div 
                      ref={profileMenuRef}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border border-gray-100"
                    >
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-[#323c5f]">
                          {currentUser.displayName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentUser.email}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#9a6ebd] text-white hover:bg-[#8a5dad] transition-colors"
                >
                  <LogIn className="w-5 h-5" />
                  <span className="font-medium">Se connecter</span>
                </button>
              )}
            </div>
          </div>

          {/* Menu mobile */}
          <div className="md:hidden flex items-center space-x-4">
            <CartIcon />
            {currentUser ? (
              <button
                onClick={handleLogout}
                className="text-gray-700 hover:text-[#9a6ebd] transition-colors"
              >
                <LogOut className="w-6 h-6" />
              </button>
            ) : (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="text-gray-700 hover:text-[#9a6ebd] transition-colors"
              >
                <LogIn className="w-6 h-6" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-[#9a6ebd] transition-colors"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Menu mobile déroulant */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {menuItems.map((item) => (
              <ScrollToTopLink
                key={item.path}
                to={item.path}
                className={`block px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-[#9a6ebd] text-white'
                    : 'text-gray-600 hover:bg-[#9a6ebd]/10 hover:text-[#9a6ebd]'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </ScrollToTopLink>
            ))}
          </div>
        </div>
      )}

      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </nav>
  );
};

export default Navbar;